import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { INewUser, ISecurityKey, IUpdateUser, ICurrentRole } from "src/common/typings/user";
import { RootState } from "../store";
import {
  getUsersAction,
  addUserAction,
  getRolesAction,
  handleAddFail,
  handleAddUser,
  handleUpdateUser,
  handleUpdateFail,
  updateUserAction,
  getUserAction,
  handleGetUser,
  handleGetFail,
  deleteUserAction,
  handleDeleteFail,
  resetPasswordAction,
  getSecurityKeys,
  getRoleByNameAction,
  addUserRoleAction,
  handleGetRoleByNameFail,
  handleGetRoleByName,
  updateUserRoleAction,
  handleGetUsers,
  handleGetUsersFail,
  getAllParentModulesAction,
  getAllChildModulesAction,
  assignRolesModulesAction,
  getRoleByIdAction,
  getSelectedModuleCheckboxesAction,
  getRolesListingAction,
  getNotificationType,
  updateNotificationType,
  getTerminalsAction,
} from "./actions/managementActions";

export interface ManagementState {
  newUsers: INewUser[];
  loading: true | false;
  status: "idle" | "loading" | "failed";
  roles: string[];
  roles_listing: string[];
  terminal_listing: string[];
  securityKeys: ISecurityKey[];
  currentUser?: IUpdateUser;
  currentUserSelectedRoles: any;
  allParentModules: string[];
  allChildModules: string[];
  allSelectedActionsAgainstModule: string[];
  currentUserRole: ICurrentRole[];
  allRoleSelectedMapping: string[];
}

const initialState: ManagementState = {
  status: "idle",
  loading: false,
  newUsers: [],
  roles: [],
  roles_listing: [],
  terminal_listing: [],
  currentUserSelectedRoles: [],
  securityKeys: [],
  allParentModules: [],
  allChildModules: ["Test"],
  allSelectedActionsAgainstModule: [],
  currentUserRole: [],
  allRoleSelectedMapping: [],
};

export const managementSlice = createSlice({
  name: "management",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetChildItems: (state: ManagementState) => {
      state.allChildModules = [];
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.

  //* Extra reducre use to handle our actions
  // we create our actions in other file and to invoke those actions with reducer we use extra reducer

  extraReducers: (builder) => {
    //  get terminal
    builder.addCase(getTerminalsAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTerminalsAction.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getTerminalsAction.fulfilled, (state, { payload }: PayloadAction<string[]>) => {
      state.terminal_listing = payload;

      state.loading = false;
    });
    //  add General methods
    builder.addCase(updateNotificationType.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateNotificationType.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateNotificationType.fulfilled, (state) => {
      state.loading = false;
    });
    // Get General methods
    builder.addCase(getNotificationType.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNotificationType.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getNotificationType.fulfilled, (state) => {
      state.loading = false;
    });

    builder
      //getUsersAction types - 3 , pending,fulfilled,rejected
      .addCase(getUsersAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUsersAction.fulfilled, handleGetUsers)
      .addCase(getUsersAction.rejected, handleGetUsersFail)

      //addUserAction types - 3 , pending,fulfilled,rejected
      .addCase(addUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addUserAction.fulfilled, handleAddUser)
      .addCase(addUserAction.rejected, handleAddFail)

      //updateUserAction types - 3
      .addCase(updateUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserAction.fulfilled, handleUpdateUser)
      .addCase(updateUserAction.rejected, handleUpdateFail)

      .addCase(getUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserAction.fulfilled, handleGetUser)
      .addCase(getUserAction.rejected, handleGetFail)

      .addCase(deleteUserAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUserAction.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteUserAction.rejected, handleDeleteFail)

      .addCase(resetPasswordAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(resetPasswordAction.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(resetPasswordAction.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getRolesAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRolesAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getRolesAction.fulfilled, (state, { payload }: PayloadAction<string[]>) => {
        state.loading = false;
        state.roles = payload;
      })
      .addCase(getRolesListingAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRolesListingAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getRolesListingAction.fulfilled, (state, { payload }: PayloadAction<string[]>) => {
        state.loading = false;
        state.roles_listing = payload;
      })

      .addCase(
        getSecurityKeys.fulfilled,
        (state: ManagementState, { payload }: PayloadAction<ISecurityKey[]>) => {
          state.securityKeys = payload;
        }
      )
      //  User Role Actions
      .addCase(addUserRoleAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(addUserRoleAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addUserRoleAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateUserRoleAction.fulfilled, (state) => {
        state.loading = true;
      })
      .addCase(updateUserRoleAction.pending, (state) => {
        state.loading = false;
      })

      .addCase(updateUserRoleAction.rejected, (state) => {
        state.loading = false;
      })

      //getRoleByIdAction

      .addCase(getRoleByIdAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRoleByIdAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(
        getRoleByIdAction.fulfilled,
        (state: ManagementState, { payload }: PayloadAction<ICurrentRole[]>) => {
          state.loading = false;
          state.currentUserRole = payload;
        }
      )
      // get All Modules
      .addCase(getAllParentModulesAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllParentModulesAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(
        getAllParentModulesAction.fulfilled,
        (state, { payload }: PayloadAction<string[]>) => {
          state.loading = false;
          state.allParentModules = payload;
        }
      )
      .addCase(getAllChildModulesAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllChildModulesAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(
        getAllChildModulesAction.fulfilled,
        (state, { payload }: PayloadAction<string[]>) => {
          state.loading = false;
          state.allChildModules = payload;
        }
      )
      .addCase(assignRolesModulesAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignRolesModulesAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(
        assignRolesModulesAction.fulfilled,
        (state, { payload }: PayloadAction<string[]>) => {
          state.loading = false;
        }
      )
      .addCase(getSelectedModuleCheckboxesAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSelectedModuleCheckboxesAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(
        getSelectedModuleCheckboxesAction.fulfilled,
        (state, { payload }: PayloadAction<string[]>) => {
          state.loading = false;
          state.allRoleSelectedMapping = payload;
        }
      )

      // Get User Role By Name
      .addCase(getRoleByNameAction.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getRoleByNameAction.fulfilled, handleGetRoleByName)
      .addCase(getRoleByNameAction.rejected, handleGetRoleByNameFail);
    // Update User Role
  },
});

export const { resetChildItems } = managementSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectStatus = (state: RootState) => state.management.status;
export const selectUsers = (state: RootState) => state.management.newUsers;
export const selectSecurityKeys = (state: RootState) => state.management.securityKeys;
export const selectRoles = (state: RootState) => state.management.roles;
export const selectRolesListing = (state: RootState) => state.management.roles_listing;
export const selectTerminalListing = (state: RootState) => state.management.terminal_listing;
export const loading = (state: RootState) => state.management.loading;
export const selectCurrentUser = (state: RootState) => state.management.currentUser;
export const selectCurrentUserSelectedRoles = (state: RootState) =>
  state.management.currentUserSelectedRoles;
export const selectAllParentModules = (state: RootState) => state.management.allParentModules;
export const selectAllChildModules = (state: RootState) => state.management.allChildModules;
export const selectCurrentUserRole = (state: RootState) => state.management.currentUserRole;
export const selectRoleSelectedMapping = (state: RootState) =>
  state.management.allRoleSelectedMapping;
